import React, { useState } from 'react';
import { Check, ChevronDown, FlaskConical, Info, Octagon, TriangleAlert } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
const HSA = 'Singapore Health Supplement Guidelines';
const HS_OVERVIEW = 'SG Health Supplements Overview — HS guidelines';
type SgVerdict = 'Permitted' | 'Permitted with conditions' | 'Exceeds limit' | 'Compound ingredient';
type SgIngredient = {
  id: string;
  name: string;
  resolved?: string;
  amount?: string;
  verdict: SgVerdict;
  limit: string;
  subIngredients?: SgIngredient[];
};
type SgGroup = {
  title: string;
  ratio: string;
  ingredients: SgIngredient[];
};
const botanical = 'Botanical ingredient. Declare the scientific name in italics, the plant part and the preparation type.';
const fibersol = 'Declared under the proprietary trade name Fibersol-2. Replace with a common or appropriate designation.';
const permittedLine = 'Permitted for use in health supplements. No positive-list restriction applies.';
const groups: SgGroup[] = [{
  title: 'Compound ingredients',
  ratio: '1/1',
  ingredients: [{
    id: 'orange-juice-powder',
    name: 'Orange Fruit Juice Powder',
    amount: '51.133%',
    verdict: 'Compound ingredient',
    limit: 'Sub-ingredients: 5',
    subIngredients: [{
      id: 'sg-orange-concentrate',
      name: 'Orange juice concentrate',
      resolved: 'ORANGE JUICE',
      amount: '35.661% by part',
      verdict: 'Permitted with conditions',
      limit: botanical
    }, {
      id: 'sg-sub-maltodextrin',
      name: 'Soluble corn fibre',
      resolved: 'RESISTANT MALTODEXTRIN',
      amount: '35.661% by part',
      verdict: 'Permitted with conditions',
      limit: fibersol
    }, {
      id: 'sg-mandarin-concentrate',
      name: 'Mandarin orange juice concentrate',
      resolved: 'MANDARIN ORANGE JUICE',
      amount: '21.298% by part',
      verdict: 'Permitted with conditions',
      limit: botanical
    }, {
      id: 'sg-orange-flavour',
      name: 'Natural orange flavour',
      resolved: 'ORANGE 820163 TRE',
      amount: '6.191% by part',
      verdict: 'Permitted with conditions',
      limit: 'Declared under the proprietary trade name Orange 820163 TRE. Replace with a common or appropriate designation.'
    }, {
      id: 'sg-sub-silicon-dioxide',
      name: 'Silicon dioxide',
      resolved: 'SILICON DIOXIDE',
      amount: '1.189% by part',
      verdict: 'Permitted',
      limit: permittedLine
    }]
  }]
}, {
  title: 'Actives',
  ratio: '3/3',
  ingredients: [{
    id: 'sg-vitamin-c',
    name: 'Vitamin C (as ascorbic acid)',
    resolved: 'ASCORBIC ACID',
    amount: '550 mg',
    verdict: 'Permitted',
    limit: 'No maximum addition level specified.'
  }, {
    id: 'sg-zinc',
    name: 'Zinc (as zinc sulfate)',
    resolved: 'ZINC SULFATE',
    amount: '40 mg, providing 15 mg elemental zinc',
    verdict: 'Permitted with conditions',
    limit: "At Singapore's 15 mg per day maximum for zinc in general adult health supplements. Declare the elemental equivalent alongside the salt."
  }, {
    id: 'sg-vitamin-a',
    name: 'Vitamin A (as retinol acetate)',
    resolved: 'RETINOL ACETATE',
    amount: '3,600 µg RE',
    verdict: 'Exceeds limit',
    limit: 'Exceeds the 1.5 mg (5,000 IU / 1,500 µg RE) daily maximum for vitamin A in general adult health supplements. Reduce to ≤ 1,500 µg RE per day.'
  }]
}, {
  title: 'Excipients',
  ratio: '3/3',
  ingredients: [{
    id: 'sg-maltodextrin',
    name: 'Soluble corn fibre',
    resolved: 'RESISTANT MALTODEXTRIN',
    amount: '34.291%',
    verdict: 'Permitted with conditions',
    limit: fibersol
  }, {
    id: 'sg-silicon-dioxide',
    name: 'Silicon dioxide',
    amount: '1.000%',
    verdict: 'Permitted',
    limit: permittedLine
  }, {
    id: 'sg-stevia',
    name: 'Stevia extract',
    resolved: 'STEVIA REBAUDIANA',
    amount: '0.500%',
    verdict: 'Permitted with conditions',
    limit: botanical
  }]
}, {
  title: 'Colours',
  ratio: '1/1',
  ingredients: [{
    id: 'sg-canthaxanthin',
    name: 'Canthaxanthin (as Lucarotin)',
    resolved: 'CANTHAXANTHIN',
    amount: '0.325%',
    verdict: 'Permitted with conditions',
    limit: 'Declared under the proprietary trade name Lucarotin. Replace with a common or appropriate designation.'
  }]
}];
const blockers = [{
  title: 'Reduce vitamin A from 3,600 µg RE to ≤ 1,500 µg RE',
  chip: 'Vitamin A acetate',
  body: 'HSA sets a maximum of 1.5 mg (5,000 IU / 1,500 µg RE) per day for vitamin A in general adult health supplements. This formulation provides 3,600 µg RE — more than twice the permitted maximum, and lower than the Australian limit of 3,000 µg RE.',
  source: HS_OVERVIEW
}];
const shouldFix = [{
  title: 'Replace proprietary trade names with common designations',
  chip: 'Fibersol-2 · Orange 820163 TRE · Lucarotin',
  body: 'Three ingredients are declared under supplier trade names. Each must be presented under a common or appropriate designation on the ingredient statement.'
}, {
  title: 'Declare the botanical ingredients in full',
  chip: 'Fruit juice concentrates · Stevia extract',
  body: 'Botanical ingredients must be declared with the scientific name in italics, the plant part used and the preparation type.'
}, {
  title: 'Replace the Nutrition Information Panel with an actives declaration',
  chip: 'Actives',
  body: 'A Nutrition Information Panel is not required for a health supplement — only the name and quantity of each active ingredient need be declared.'
}];
export function SgFormulaTab() {
  const [openCompound, setOpenCompound] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    'sg-vitamin-a': true
  });
  return <div className="space-y-5" data-id="element-2370" data-mp="2szyfs1anils7">
      <section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" aria-labelledby="sg-formula-heading" data-id="element-2371" data-mp="2o26kcxasshj9">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-2372" data-mp="2j2tqvtpf8c7p">
          <div className="flex items-start gap-3" data-id="element-2373" data-mp="1cokqwzxwntrx">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-2374" data-mp="17og5qwm1yavd">
              <FlaskConical className="h-5 w-5" data-id="element-2375" data-mp="1953mzajhxl40" />
            </span>
            <div data-id="element-2376" data-mp="193sonq8tl5ka">
              <h2 id="sg-formula-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-2377" data-mp="1e4gbdzq0n08p">Ingredients analysis</h2>
              <p className="mt-1 text-sm text-[#718174]" data-id="element-2378" data-mp="103vvpcfedkjf">Assessed against the Singapore Health Supplement Guidelines.</p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-800" data-id="element-2379" data-mp="qf44x7m2p6a3">
            High risk
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs font-semibold" data-id="element-2380" data-mp="hf8oan9r0j2o">
          <span className="text-red-700" data-id="element-2381" data-mp="lfj6cyatezxw">1 Blocker</span>
          <span className="text-amber-700" data-id="element-2382" data-mp="ldyc79sexj0k">3 Should fix</span>
          <span className="text-[#718174]" data-id="element-2383" data-mp="7hb4qd2u3age">0 Consider</span>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-[#56685c]" data-id="element-2384" data-mp="cfw3e82ftm2q">
          Vitamin A is more than twice the daily maximum permitted for general adult health supplements in Singapore. The remaining issues are declaration-facing — proprietary trade names and botanical ingredients must be presented under recognised designations.
        </p>
      </section>

      <section aria-label="Blocker" className="space-y-3" data-id="element-2385" data-mp="2pnrmzbh3l8w">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-red-700" data-id="element-2386" data-mp="3tx09zho3e4qe">Blocker · 1</h2>
        {blockers.map(item => <article key={item.title} className="rounded-2xl border-2 border-red-300 border-l-[6px] border-l-red-600 bg-red-50 p-5 shadow-sm" data-id="element-2387" data-mp="bq2j6gf15wku">
            <div className="flex items-start gap-3" data-id="element-2388" data-mp="lh6u951l4xae">
              <Octagon className="mt-0.5 h-5 w-5 shrink-0 fill-red-600 text-red-600" data-id="element-2389" data-mp="lk2s6tdfnlyu" />
              <div className="min-w-0" data-id="element-2390" data-mp="gl8axvv465gu">
                <h3 className="font-[Figtree] text-base font-semibold text-red-950" data-id="element-2391" data-mp="uietbpbxe25j">{item.title}</h3>
                <span className="mt-2 inline-flex rounded-full bg-white/70 px-2.5 py-1 text-xs font-medium text-red-900" data-id="element-2392" data-mp="qhut2cw22if6">{item.chip}</span>
                <p className="mt-3 text-sm leading-relaxed text-red-900" data-id="element-2393" data-mp="1wvrcqiq30bl6">{item.body}</p>
                <div className="mt-3" data-id="element-2394" data-mp="21u2hif1irvwj">
                  <RegulationLink text={item.source} data-id="element-2395" data-mp="21x6sbv9hppw6" />
                </div>
              </div>
            </div>
          </article>)}
      </section>

      <section aria-label="Should fix" className="space-y-3" data-id="element-2396" data-mp="2aur0lo5rsmga">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-800" data-id="element-2397" data-mp="1e6b7xj7d5wyy">Should fix · 3</h2>
        {shouldFix.map(item => <article key={item.title} className="rounded-2xl border border-[#dce3da] border-l-4 border-l-amber-400 bg-white p-5 shadow-sm" data-id="element-2398" data-mp="1j2rvab6qo7az">
            <div className="flex items-start gap-3" data-id="element-2399" data-mp="1rxprbr56o06j">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-2400" data-mp="1ryhdkbcw4lk5" />
              <div className="min-w-0" data-id="element-2401" data-mp="veibfet35tmh">
                <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2402" data-mp="zgvji9vb23mf">{item.title}</h3>
                <span className="mt-2 inline-flex rounded-full bg-[#f0f3ef] px-2.5 py-1 text-xs font-medium text-[#617366]" data-id="element-2403" data-mp="rxduigzaklih">{item.chip}</span>
                <p className="mt-3 text-sm leading-relaxed text-[#56685c]" data-id="element-2404" data-mp="mzcey612r4br">{item.body}</p>
                <div className="mt-3" data-id="element-2405" data-mp="ivwfz31j8rvw">
                  <RegulationLink text={HSA} data-id="element-2406" data-mp="iuuyafq0h757" />
                </div>
              </div>
            </div>
          </article>)}
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="sg-verdicts-heading" data-id="element-2407" data-mp="8z1i8md32vio">
        <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-2408" data-mp="468dvzlj1p74">
          <h2 id="sg-verdicts-heading" className="inline-flex items-center gap-2 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-2409" data-mp="3vdb8z24hkfmm">
            <Info className="h-4 w-4 text-[#738478]" data-id="element-2410" data-mp="3re2chpi2a3nd" />
            Ingredient verdicts
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#718174]" data-id="element-2411" data-mp="3mcmts43cw985">
            Singapore does not operate a closed permitted-ingredients list for health supplements. Ingredients that could not be resolved against the Australian Determination resolve cleanly here, subject to declaration requirements.
          </p>
          <p className="mt-3 font-mono text-xs font-semibold uppercase tracking-[0.11em] text-[#6f8174]" data-id="element-2412" data-mp="belfyup3dawd">
            3 permitted · 8 permitted with conditions · 1 exceeds limit
          </p>
        </div>

        <div className="space-y-5 p-5" data-id="element-2413" data-mp="3evbs7rb9ok6i">
          {groups.map(group => <div key={group.title} className="overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-2414" data-mp="3exega5sv038l">
              <div className="flex items-center justify-between bg-[#f4f7f2] px-4 py-2.5" data-id="element-2415" data-mp="3avt8gyv6mf59">
                <p className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-2416" data-mp="3orequn6wu0op">{group.title}</p>
                <span className="font-mono text-xs font-semibold text-[#728477]" data-id="element-2417" data-mp="3jwhkxp4xg7g1">{group.ratio}</span>
              </div>
              <div className="divide-y divide-[#edf0eb]" data-id="element-2418" data-mp="z460vj4t2i8a">
                {group.ingredients.map(ingredient => <IngredientRow key={ingredient.id} ingredient={ingredient} openCompound={openCompound} onToggleCompound={() => setOpenCompound(open => !open)} expanded={expanded} onToggle={id => setExpanded(state => ({
              ...state,
              [id]: !state[id]
            }))} data-id="element-2419" data-mp="2h1arualzjxrb" />)}
              </div>
            </div>)}
        </div>
      </section>
    </div>;
}
function IngredientRow({
  ingredient,
  openCompound,
  onToggleCompound,
  expanded,
  onToggle,
  nested = false
}: {
  ingredient: SgIngredient;
  openCompound: boolean;
  onToggleCompound: () => void;
  expanded: Record<string, boolean>;
  onToggle: (id: string) => void;
  nested?: boolean;
}) {
  const isCompound = ingredient.verdict === 'Compound ingredient';
  const isOpen = Boolean(expanded[ingredient.id]);
  return <div className={nested ? 'bg-[#fbfdf9]' : 'bg-white'} data-id="element-2420" data-mp="3og5q46dz3dy6">
      <div className={`flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-start lg:justify-between ${nested ? 'pl-8' : ''}`} data-id="element-2421" data-mp="25ou4kftekgp">
        <div className="min-w-0 flex-1" data-id="element-2422" data-mp="3tdb6r2kihx9p">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-2423" data-mp="3ta6vxmcjk3a2">
            <p className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-2424" data-mp="3oblqn8ifmter">{ingredient.name}</p>
            {ingredient.resolved && <>
                <span className="text-[#a1ada2]" data-id="element-2425" data-mp="3fd0djsvqc6ld">→</span>
                <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#718174]" data-id="element-2426" data-mp="3fel7phe4tnip">{ingredient.resolved}</span>
              </>}
            {ingredient.amount && <span className="text-sm text-[#718174]" data-id="element-2427" data-mp="35ngv2obxier1">— {ingredient.amount}</span>}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-[#748477]" data-id="element-2428" data-mp="30ox4game4esc">{ingredient.limit}</p>
          {!isCompound && <>
              <p className="mt-2 text-xs text-[#8b968d]" data-id="element-2429" data-mp="2vqc50deypz4y">
                Source: <RegulationLink text={HSA} className="align-middle" data-id="element-2430" data-mp="2rnfsn4g0mz9g" />
              </p>
              {isOpen && <div className="mt-2 rounded-lg bg-[#f4f7f2] p-3 text-xs leading-relaxed text-[#607266]" data-id="element-2431" data-mp="174lt3zkiqsvu">
                  Verdicts are assessed against the Singapore Health Supplement Guidelines using the declared ingredient name, form and quantity per daily dose.
                </div>}
            </>}
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end" data-id="element-2432" data-mp="nhebztzzr9e2">
          <SgBadge verdict={ingredient.verdict} data-id="element-2433" data-mp="nf1koq8cfmoc" />
          {isCompound ? <button type="button" onClick={onToggleCompound} aria-expanded={openCompound} className="rounded-lg p-1.5 text-[#718174] hover:bg-[#f2f5f1]" data-id="element-2434" data-mp="sg8ron2nwhty">
              <ChevronDown className={`h-4 w-4 transition ${openCompound ? 'rotate-180' : ''}`} data-id="element-2435" data-mp="12622vmyw6uxo" />
            </button> : <button type="button" onClick={() => onToggle(ingredient.id)} aria-expanded={isOpen} className="rounded-lg p-1.5 text-[#718174] hover:bg-[#f2f5f1]" data-id="element-2436" data-mp="4pv87ky57raz">
              <ChevronDown className={`h-4 w-4 transition ${isOpen ? 'rotate-180' : ''}`} data-id="element-2437" data-mp="2bdvm7dejehvc" />
            </button>}
        </div>
      </div>
      {isCompound && openCompound && ingredient.subIngredients && <div className="border-t border-[#e6ece4] bg-[#f5f8f3] px-4 py-3" data-id="element-2438" data-mp="2l5i2vp9n995h">
          <p className="pl-4 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-2439" data-mp="2gd86prhxrcur">Sub-ingredients</p>
          <div className="mt-2 overflow-hidden rounded-lg border border-[#dfe7dc] bg-white" data-id="element-2440" data-mp="2v1bdh33syskm">
            {ingredient.subIngredients.map(sub => <IngredientRow key={sub.id} ingredient={sub} openCompound={false} onToggleCompound={() => undefined} expanded={expanded} onToggle={onToggle} nested data-id="element-2441" data-mp="343c95vje16k6" />)}
          </div>
        </div>}
    </div>;
}
function SgBadge({
  verdict
}: {
  verdict: SgVerdict;
}) {
  if (verdict === 'Permitted') return <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-2442" data-mp="14r05jecegz8q">
        <Check className="h-3.5 w-3.5" data-id="element-2443" data-mp="3gfn3oeip2119" />
        Permitted
      </span>;
  if (verdict === 'Permitted with conditions') return <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-2444" data-mp="37li518xbjlb6">Permitted with conditions</span>;
  if (verdict === 'Exceeds limit') return <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800" data-id="element-2445" data-mp="3bl0n7wxr9iab">Exceeds limit</span>;
  return <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-2446" data-mp="2xpf1pzqqdgmn">Compound ingredient</span>;
}